import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { useOnClickOutside } from '@hooks/useOnClickOutside'
import { noop } from '@utils/noop'
import { FC, ReactNode, useRef, useState } from 'react'
import { twMerge } from '@lib/tailwind-merge'
import { Button, Props as ButtonProps } from './Button'
import { DropdownMenu, DropdownMenuProps } from 'components/menus'

interface Props {
  beforeMenuItems?: ReactNode
  button?: (args: {
    disabled: boolean
    menuHidden: boolean

    // Consumers can either invoke toggleMenuHidden directly,
    // or pass the clickHandler directly to a custom button element's onClick,
    // which stops propagation if preventEventPropagation is true,
    // and then invokes toggleMenuHidden
    toggleMenuHidden: () => void
    clickHandler: (e: React.MouseEvent) => void
  }) => ReactNode
  buttonVariant?: ButtonProps['variant']
  children?: ReactNode
  className?: string | ((options: { hidden: boolean }) => string)
  closeOnMouseLeave?: boolean
  closeMenuOnItemClick?: boolean
  disabled?: boolean
  dropdownMenuHeader?: string
  menuItems: DropdownMenuProps['items']
  onMenuHiddenChange?: (hidden: boolean) => void
  preventEventPropagation?: boolean
  testid?: string
  title?: string
}

export const MenuButton: FC<Props> = ({
  beforeMenuItems,
  button,
  buttonVariant,
  children,
  className,
  closeOnMouseLeave = false,
  closeMenuOnItemClick = true,
  disabled = false,
  dropdownMenuHeader,
  menuItems,
  onMenuHiddenChange = noop,
  preventEventPropagation = false,
  testid = 'menu-button-wrapper',
  title,
}) => {
  const [menuHidden, setMenuHidden] = useState(true)

  const ref = useRef(null)

  const closeMenu = () => {
    setMenuHidden(true)
    onMenuHiddenChange(true)
  }

  const toggleMenu = () => {
    setMenuHidden((hidden) => {
      onMenuHiddenChange(!hidden)
      return !hidden
    })
  }

  const clickHandler = (e: React.MouseEvent) => {
    if (preventEventPropagation) e.stopPropagation()
    toggleMenu()
  }

  useOnClickOutside(ref, closeMenu)

  const renderButton = () => {
    if (!button)
      return (
        <Button
          data-tooltip-id={title}
          disabled={disabled}
          onClick={clickHandler}
          icon={menuHidden ? faChevronDown : faChevronUp}
          title={title}
          variant={buttonVariant}
        >
          {children}
        </Button>
      )

    return button({
      toggleMenuHidden: toggleMenu,
      disabled,
      menuHidden,
      clickHandler,
    })
  }

  return (
    <div
      className={twMerge(
        'relative flex items-center text-sm justify-items-end w-max',
        typeof className === 'function'
          ? className({ hidden: menuHidden })
          : className,
      )}
      ref={ref}
      data-testid={testid}
    >
      {renderButton()}
      <DropdownMenu
        beforeItems={beforeMenuItems}
        className="top-[120%] z-30"
        header={dropdownMenuHeader}
        hidden={menuHidden}
        items={menuItems}
        onItemClick={() => closeMenuOnItemClick && closeMenu()}
        onMouseLeave={() => closeOnMouseLeave && closeMenu()}
        preventEventPropagation={preventEventPropagation}
      />
    </div>
  )
}
